export interface PumpProperty {
    Code: Nullable<string>;
    Description: Nullable<string>;
    Standard?: boolean;
}

export const enum Horsepower {
    ONE = '01',
    TWO = '02',
    THREE = '03',
    FIVE = '05',
    SEVEN = '07',
    TEN = '10',
    FIFTEEN = '15'
}

export const enum Frequency {
    A = 'A',
    B = 'B'
}

export enum ImpellerMaterial {
    C = 'C',
    BM = 'BM',
    S = 'S',
    M = 'M'
}

export enum LowerSealMaterial {
    S = 'S',
    T = 'T'
}

export enum DischargeType {
    CC = 'CC',
    EC = 'EC',
    BJ = 'BJ',
    FB = 'FB'
}

export enum CordType {
    A = 'A',
    B = 'B',
    C = 'C'
}

export class LEPPumpViewModel {
    private _solidsHandlingSize: Nullable<number>;
    private _solidsHandlingSizeOptions: PumpProperty[];
    private _pumpType: Nullable<string>;
    private _pumpTypeDesc: Nullable<string>;
    private _sealType: Nullable<string>;
    private _horsepower: Nullable<Horsepower>;
    private _horsepowerDesc: Nullable<string>;
    private _voltage: Nullable<number>;
    private _voltageOptions: PumpProperty[];
    private _operationType: Nullable<string>;
    private _operationTypeDesc: Nullable<string>;
    private _frequency: Nullable<Frequency>;
    private _frequencyOptions: PumpProperty[];
    private _impellerSize: Nullable<string>;
    private _impellerSizeOptions: PumpProperty[];
    private _impellerMaterial: Nullable<ImpellerMaterial>;
    private _impellerMaterialOptions: PumpProperty[];
    private _sealConfig: Nullable<string>;
    private _sealConfigOptions: PumpProperty[];
    private _lowerSealMaterial: Nullable<LowerSealMaterial>;
    private _lowerSealMaterialOptions: PumpProperty[];
    private _discharge: Nullable<DischargeType>;
    private _dischargeOptions: PumpProperty[];
    private _cordType: Nullable<CordType>;
    private _cordTypeOptions: PumpProperty[];
    private _cordLength: Nullable<string>;
    private _cordLengthOptions: PumpProperty[];
    private _capacitor: Nullable<boolean>;
    private _capacitorOptions: PumpProperty[];
    private _verticalDischarge: Nullable<boolean>;
    private _dischargeOrientationOptions: PumpProperty[];

    constructor() {
        this._solidsHandlingSize = null;
        this._solidsHandlingSizeOptions = [];
        this._pumpType = null;
        this._pumpTypeDesc = null;
        this._sealType = null;
        this._horsepower = null;
        this._horsepowerDesc = null;
        this._voltage = null;
        this._voltageOptions = [];
        this._operationType = null;
        this._operationTypeDesc = null;
        this._frequency = null;
        this._frequencyOptions = [];
        this._impellerSize = null;
        this._impellerSizeOptions = [];
        this._impellerMaterial = null;
        this._impellerMaterialOptions = [];
        this._sealConfig = null;
        this._sealConfigOptions = [];
        this._lowerSealMaterial = null;
        this._lowerSealMaterialOptions = [];
        this._discharge = null;
        this._dischargeOptions = [];
        this._cordType = null;
        this._cordTypeOptions = [];
        this._cordLength = null;
        this._cordLengthOptions = [];
        this._capacitor = null;
        this._capacitorOptions = [];
        this._verticalDischarge = null;
        this._dischargeOrientationOptions = [];
    }

    public get solidsHandlingSize(): Nullable<number> {
        return this._solidsHandlingSize;
    }

    public set solidsHandlingSize(size: Nullable<number>) {
        this._solidsHandlingSize = size;
    }

    public get solidsHandlingSizeOptions(): PumpProperty[] {
        return this._solidsHandlingSizeOptions;
    }

    public set solidsHandlingSizeOptions(options: PumpProperty[]) {
        this._solidsHandlingSizeOptions = options; 
    }

    public get pumpType(): Nullable<string> {
        return this._pumpType;
    }

    public set pumpType(pumpType: Nullable<string>) {
        this._pumpType = pumpType;
    }

    /**
     * A "small" grinder is a 2.5 HP grinder. These are often treated like 
     * single seal models - even though they're dual seal.
     */
    public get isSmallGrinder() : boolean {
        return !this._solidsHandlingSize && this._horsepower === Horsepower.TWO
    }

    public get pumpTypeDesc(): Nullable<string> {
        return this._pumpTypeDesc;
    }

    public set pumpTypeDesc(pumpTypeDesc: Nullable<string>) {
        this._pumpTypeDesc = pumpTypeDesc;
    }

    public get sealType(): Nullable<string> {
        return this._sealType;
    }

    public set sealType(sealType: Nullable<string>) {
        this._sealType = sealType;
    }

    public get isSingleSeal(): boolean {
        return this._sealType === 'Single Seal';
    }

    public get horsepower(): Nullable<Horsepower> {
        return this._horsepower;
    }

    public set horsepower(horsepower: Nullable<Horsepower>) {
        this._horsepower = horsepower;
    }

    public get horsepowerDesc(): Nullable<string> {
        return this._horsepowerDesc;
    }
    
    public set horsepowerDesc(horsepowerDesc: Nullable<string>) {
        this._horsepowerDesc = horsepowerDesc;
    }

    public get voltage(): Nullable<number> {
        return this._voltage;
    }

    public set voltage(value: Nullable<number>) {
        this._voltage = value;
        if (this.isThreePhase) {
            // when the voltage changes and is a three phase voltage, set the capacitor to
            // false since this is not available to three phase models.
            this.capacitor = false;
        }
    }

    /**
     * Currently, voltage codes for single phase voltages are '2' and '8'.
     * If the model has a voltage code other than these two, it's a three phase
     * model.
     */
    public get isThreePhase(): boolean {
        return this._voltage !== 2 && this._voltage !== 8;
    }

    public get voltageOptions(): PumpProperty[] {
        return this._voltageOptions;
    }

    public set voltageOptions(voltageOptions: PumpProperty[]) {
        this._voltageOptions = voltageOptions;
    }

    public get operationType(): Nullable<string> {
        return this._operationType;
    }

    public set operationType(operationType: Nullable<string>) {
        this._operationType = operationType;
    }

    public get operationTypeDesc(): Nullable<string> {
        return this._operationTypeDesc;
    }

    public set operationTypeDesc(operationTypeDesc: Nullable<string>) {
        this._operationTypeDesc = operationTypeDesc;
    }

    public get frequency(): Nullable<Frequency> {
        return this._frequency;
    }

    public set frequency(frequency: Nullable<Frequency>) {
        this._frequency = frequency;
    }

    public get frequencyOptions(): PumpProperty[] {
        return this._frequencyOptions;
    }

    public set frequencyOptions(options: PumpProperty[]) {
        this._frequencyOptions = options;
    }

    public get impellerSize(): Nullable<string> {
        return this._impellerSize;
    }

    public set impellerSize(value: Nullable<string>) {
        this._impellerSize = value;
    }

    public get impellerSizeOptions(): PumpProperty[] {
        return this._impellerSizeOptions;
    }

    public set impellerSizeOptions(options: PumpProperty[]) {
        this._impellerSizeOptions = options;
    }

    public get impellerMaterial(): Nullable<ImpellerMaterial> {
        return this._impellerMaterial;
    }

    public set impellerMaterial(impellerMaterial: Nullable<ImpellerMaterial>) {
        this._impellerMaterial = impellerMaterial;
    }

    public get impellerMaterialOptions(): PumpProperty[] {
        return this._impellerMaterialOptions;
    }

    public set impellerMaterialOptions(options: PumpProperty[]) {
        this._impellerMaterialOptions = options;
    }

    public get sealConfig(): Nullable<string> {
        return this._sealConfig;
    }

    public set sealConfig(value: Nullable<string>) {
        this._sealConfig = value;
    }

    public get sealConfigOptions(): PumpProperty[] {
        return this._sealConfigOptions;
    }

    public set sealConfigOptions(options: PumpProperty[]) {
        this._sealConfigOptions = options;
    }

    public get lowerSealMaterial(): Nullable<LowerSealMaterial> {
        return this._lowerSealMaterial;
    }

    public set lowerSealMaterial(value: Nullable<LowerSealMaterial>) {
        this._lowerSealMaterial = value;
    }

    public get lowerSealMaterialOptions(): PumpProperty[] {
        return this._lowerSealMaterialOptions;
    }

    public set lowerSealMaterialOptions(options: PumpProperty[]) {
        this._lowerSealMaterialOptions = options;
    }

    public get discharge(): Nullable<DischargeType> {
        return this._discharge;
    }

    public set discharge(value: Nullable<DischargeType>) {
        this._discharge = value;
    }

    public get dischargeOptions(): PumpProperty[] {
        return this._dischargeOptions;
    }

    public set dischargeOptions(options: PumpProperty[]) {
        this._dischargeOptions = options;
    }

    public get cordType(): Nullable<CordType> {
        return this._cordType;
    }

    public set cordType(value: Nullable<CordType>) {
        this._cordType = value;
    }

    public get cordTypeOptions(): PumpProperty[] {
        return this._cordTypeOptions;
    }

    public set cordTypeOptions(options: PumpProperty[]) {
        this._cordTypeOptions = options;
    }

    public get cordLength(): Nullable<string> {
        return this._cordLength;
    }

    public set cordLength(cordLength: Nullable<string>) {
        this._cordLength = cordLength;
    }

    public get cordLengthOptions(): PumpProperty[] {
        return this._cordLengthOptions;
    }

    public set cordLengthOptions(cordLengthOptions: PumpProperty[]) {
        this._cordLengthOptions = cordLengthOptions;
    }

    public get capacitor(): Nullable<boolean> {
        return this._capacitor;
    }

    public set capacitor(hasCapacitor: Nullable<boolean>) {
        this._capacitor = hasCapacitor;
    }

    public get capacitorOptions(): PumpProperty[] {
        return this._capacitorOptions;
    }

    public set capacitorOptions(options: PumpProperty[]) {
        this._capacitorOptions = options;
    }

    public get verticalDischarge() : Nullable<boolean> {
        return this._verticalDischarge;
    }

    public set verticalDischarge(hasVerticalDischarge: Nullable<boolean>) {
        this._verticalDischarge = hasVerticalDischarge;
    }

    public get dischargeOrientationOptions(): PumpProperty[] {
        return this._dischargeOrientationOptions;
    }

    public set dischargeOrientationOptions(options: PumpProperty[]) {
        this._dischargeOrientationOptions = options;
    }

    public getModelNumber = () : string => {
        return `${this.solidsHandlingSize ? this.solidsHandlingSize : ''}${this.pumpType}${this.horsepower}${this.voltageDisplay()}${this.frequency ? this.frequency : ''}${this.impellerSize ? this.impellerSize : ''}${this.impellerMaterial ? this.impellerMaterial : ''}${this.sealConfig ? this.sealConfig : ''}${this.lowerSealMaterial ? this.lowerSealMaterial : ''}${this.discharge ? this.discharge : ''}${this.operationType ? this.operationType : ''}${this.cordType ? this.cordType : ''}${this.dischargeOrientationDisplay()}${this.cordLength}${this.capacitorDisplay()}`;
    }

    public complete = () : boolean => {
        if (!this._pumpType || !this._horsepower || !this.voltage || !this._cordLength) {
            return false;
        }
        return true;
    }

    private voltageDisplay = () : string => {
        if (!this.voltage) {
            return '_';
        }
        return this.voltage.toString();
    }

    public capacitorDisplay = () : string => {
        if (!this.capacitor) {
            return '';
        }
        return this.capacitor ? 'C' : '';
    }

    public dischargeOrientationDisplay = () : string => {
        if (!this.verticalDischarge) {
            return '';
        }
        return this.verticalDischarge ? 'V' : '';
    }
};
